import { useSnackbar } from 'notistack';
import { useEffect, useLayoutEffect, useRef } from 'react';
import { useMutation } from 'react-query';
import LoadingWrapper from 'src/components/Loading/LoadingWrapper';

import EventService from 'src/services/EventService';
import SessionService from 'src/services/SessionService';
import useUserStore from 'src/stores/user';
import useSessionStore from 'src/stores/session';

import { getBeginWorkNextStepId, getBeginWorkStepByOrder, getBeginWorkStepOrder } from 'src/utils/beginWork';
import { BEGIN_WORK_STEPS_IDS } from 'src/constants/beginWorksSteps';
import { ROUTES_URL } from 'src/constants/routes';

import useGoTo from 'src/hooks/useGoTo';

import { history } from 'src/utils/history';

import type { StepsType } from 'src/types/User';

export const UPDATE_BEGIN_WORK_STEP_EVENT_TYPE = 'updateBeginWorkStep';

interface IUpdateBeginWorkStepsProps {
    children: React.ReactNode;
}

interface IUpdateBeginWorkStepEvent {
    currentStep: StepsType;
    decrease?: boolean;
    onSuccess?: () => void | Promise<void>;
}

export const updateBeginWorkStep = (params: IUpdateBeginWorkStepEvent) => {
    console.log(`📢 [updateBeginWorkStep] Отправляем событие:`, params);
    console.log(`📢 [updateBeginWorkStep] Тип события: ${UPDATE_BEGIN_WORK_STEP_EVENT_TYPE}`);
    console.log(`📢 [updateBeginWorkStep] currentStep: ${params.currentStep}, тип: ${typeof params.currentStep}`);
    EventService.dispatch(UPDATE_BEGIN_WORK_STEP_EVENT_TYPE, params);
    console.log(`✅ [updateBeginWorkStep] Событие отправлено через EventService.dispatch`);
};

export default function UpdateBeginWorkStepsProvider({ children }: IUpdateBeginWorkStepsProps) {
    const { enqueueSnackbar } = useSnackbar();
    const goTo = useGoTo();

    const {
        updateStep: updateOnboardingStep,
        isOnboardingCompleted,
        isAdminOverride,
        onboardingStep,
    } = useUserStore(({ updateStep, isOnboardingCompleted, isAdminOverride, onboardingStep }) => ({
        updateStep,
        isOnboardingCompleted,
        isAdminOverride,
        onboardingStep,
    }));
    const hasSessionSnapshot = useSessionStore(({ hasSessionSnapshot }) => hasSessionSnapshot);

    const justUpdatedStepRef = useRef(false);

    const { mutateAsync: updateStep, isLoading } = useMutation(SessionService.queryKey, updateOnboardingStep, {
        onError: (error: any) => {
            console.info(error);
            // Проверяем, не является ли ошибка следствием того, что шаг уже обновлен
            const errorMessage = error?.response?.data?.error || error?.response?.data?.message || error?.message || '';
            const isAlreadyUpdated =
                errorMessage.includes('Cannot skip steps') ||
                errorMessage.includes('already') ||
                errorMessage.includes('already updated') ||
                (error?.response?.status === 400 &&
                    (errorMessage.includes('already') || errorMessage.includes('Cannot skip')));

            // Если ошибка связана с тем, что шаг уже обновлен - не показываем ошибку
            if (!isAlreadyUpdated) {
                // Показываем ошибку только для реальных проблем, не для уже обновленных шагов
                console.warn('Ошибка обновления онбоардинга:', errorMessage);
                // Не показываем snackbar для ошибок обновления шага - они не критичны
                // enqueueSnackbar('Ошибка обновления онбоардинга', { variant: 'error' });
            } else {
                console.log('Шаг онбординга уже обновлен, ошибка проигнорирована');
            }
            justUpdatedStepRef.current = false;
        },
        onSuccess: () => {
            // Помечаем, что шаг только что обновлен
            justUpdatedStepRef.current = true;
            // Сбрасываем флаг через небольшую задержку
            setTimeout(() => {
                justUpdatedStepRef.current = false;
            }, 500);
        },
    });

    useEffect(() => {
        if (isOnboardingCompleted) {
            return;
        }

        const subscriber = EventService.subscribe(
            UPDATE_BEGIN_WORK_STEP_EVENT_TYPE,
            async (_type, { currentStep, decrease, onSuccess }: IUpdateBeginWorkStepEvent) => {
                console.log(`🚨 [UpdateBeginWorkStepsProvider] СОБЫТИЕ ПОЛУЧЕНО!`, {
                    _type,
                    currentStep,
                    decrease,
                    onSuccess: !!onSuccess,
                    isLoading,
                    onboardingStep,
                });

                // Защита от повторных вызовов
                console.log(`🔍 [UpdateBeginWorkStepsProvider] Проверка isLoading: ${isLoading}`);
                if (isLoading) {
                    console.log(`⏳ [UpdateBeginWorkStepsProvider] Запрос уже выполняется, пропускаем`);
                    return;
                }

                // НЕ блокируем обработку события из-за hasSessionSnapshot
                // Это проверка нужна только для автоматического редиректа, а не для явных переходов
                console.log(`📥 [UpdateBeginWorkStepsProvider] Получено событие обновления шага:`, {
                    currentStep,
                    decrease,
                    onboardingStep,
                    hasSessionSnapshot,
                    'typeof currentStep': typeof currentStep,
                    'currentStep value': currentStep,
                });

                // currentStep может быть:
                // 1. Текущим шагом, от которого нужно перейти к следующему (для обычных переходов)
                // 2. Целевым шагом для развилки (например, create_project или choose_stream от format)
                let stepToUpdate: StepsType | null = null;

                // Получаем реальный текущий шаг пользователя из store
                const realCurrentStep = useUserStore.getState().onboardingStepName;

                // КРИТИЧЕСКАЯ ПРОВЕРКА: проверяем развилку ПЕРЕД основной логикой
                // Преобразуем currentStep в строку для надежного сравнения
                const currentStepStr = currentStep ? String(currentStep).trim().toLowerCase() : '';
                const createProjectValue = BEGIN_WORK_STEPS_IDS.CREATE_PROJECT.trim().toLowerCase();
                const chooseStreamValue = BEGIN_WORK_STEPS_IDS.CHOOSE_STREAM.trim().toLowerCase();

                // Множественные проверки для надежности
                const isCreateProject =
                    currentStepStr === createProjectValue ||
                    currentStep === BEGIN_WORK_STEPS_IDS.CREATE_PROJECT ||
                    String(currentStep) === String(BEGIN_WORK_STEPS_IDS.CREATE_PROJECT) ||
                    (typeof currentStep === 'string' && currentStep.toLowerCase() === 'create_project');

                const isChooseStream =
                    currentStepStr === chooseStreamValue ||
                    currentStep === BEGIN_WORK_STEPS_IDS.CHOOSE_STREAM ||
                    String(currentStep) === String(BEGIN_WORK_STEPS_IDS.CHOOSE_STREAM) ||
                    (typeof currentStep === 'string' && currentStep.toLowerCase() === 'choose_stream');

                console.log(`🔍 [UpdateBeginWorkStepsProvider] КРИТИЧЕСКАЯ ПРОВЕРКА РАЗВИЛКИ:`, {
                    currentStep,
                    'typeof currentStep': typeof currentStep,
                    currentStepStr,
                    createProjectValue,
                    chooseStreamValue,
                    isCreateProject,
                    isChooseStream,
                    'BEGIN_WORK_STEPS_IDS.CREATE_PROJECT': BEGIN_WORK_STEPS_IDS.CREATE_PROJECT,
                    'BEGIN_WORK_STEPS_IDS.CHOOSE_STREAM': BEGIN_WORK_STEPS_IDS.CHOOSE_STREAM,
                    'String(currentStep)': String(currentStep),
                    'String(BEGIN_WORK_STEPS_IDS.CREATE_PROJECT)': String(BEGIN_WORK_STEPS_IDS.CREATE_PROJECT),
                    realCurrentStep,
                });

                // ПРИОРИТЕТ 1: Если currentStep это CREATE_PROJECT или CHOOSE_STREAM - это развилка от format
                // Это явный вызов с кнопки, поэтому всегда разрешаем развилку
                // Упрощенная проверка: сравниваем напрямую со строковыми значениями
                const currentStepNormalized = String(currentStep).trim().toLowerCase();
                const isCreateProjectSimple =
                    currentStepNormalized === 'create_project' || currentStep === BEGIN_WORK_STEPS_IDS.CREATE_PROJECT;
                const isChooseStreamSimple =
                    currentStepNormalized === 'choose_stream' || currentStep === BEGIN_WORK_STEPS_IDS.CHOOSE_STREAM;

                if (isCreateProjectSimple || isChooseStreamSimple || isCreateProject || isChooseStream) {
                    // Это развилка от format: используем currentStep как целевой шаг
                    // Это явный вызов с кнопки, поэтому всегда разрешаем развилку
                    // Бэкенд проверит, что текущий шаг format
                    if (isCreateProjectSimple || isCreateProject) {
                        stepToUpdate = BEGIN_WORK_STEPS_IDS.CREATE_PROJECT as StepsType;
                    } else if (isChooseStreamSimple || isChooseStream) {
                        stepToUpdate = BEGIN_WORK_STEPS_IDS.CHOOSE_STREAM as StepsType;
                    } else {
                        // Fallback: используем currentStep напрямую
                        stepToUpdate = currentStep as StepsType;
                    }
                    console.log(
                        `✅ [UpdateBeginWorkStepsProvider] РАЗВИЛКА ОПРЕДЕЛЕНА: используем ${stepToUpdate} как целевой шаг (currentStep был: ${currentStep})`
                    );
                    console.log(
                        `🔄 [UpdateBeginWorkStepsProvider] Развилка от format: ${realCurrentStep} → ${stepToUpdate}`
                    );
                    console.log(
                        `🔍 [UpdateBeginWorkStepsProvider] Проверки: isCreateProjectSimple=${isCreateProjectSimple}, isChooseStreamSimple=${isChooseStreamSimple}, isCreateProject=${isCreateProject}, isChooseStream=${isChooseStream}`
                    );
                } else if (currentStep) {
                    // Если не развилка, проверяем другие случаи
                    if (
                        currentStep === BEGIN_WORK_STEPS_IDS.FORMAT ||
                        currentStepStr === BEGIN_WORK_STEPS_IDS.FORMAT.trim().toLowerCase()
                    ) {
                        // Разрешаем переход на format от create_project или choose_stream (возврат к выбору пути)
                        if (
                            realCurrentStep === BEGIN_WORK_STEPS_IDS.CREATE_PROJECT ||
                            realCurrentStep === BEGIN_WORK_STEPS_IDS.CHOOSE_STREAM
                        ) {
                            stepToUpdate = BEGIN_WORK_STEPS_IDS.FORMAT as StepsType;
                            console.log(
                                `✅ [UpdateBeginWorkStepsProvider] Возврат к format от ${realCurrentStep} для выбора пути`
                            );
                        } else {
                            // Если текущий шаг format, но не указан целевой шаг развилки - это ошибка
                            // Не должны вызывать updateBeginWorkStep с currentStep=format без указания целевого шага
                            console.error(
                                `❌ [UpdateBeginWorkStepsProvider] Нельзя обновить шаг format без указания целевого шага (create_project или choose_stream)`
                            );
                            stepToUpdate = null;
                        }
                    } else {
                        // Для обычных шагов находим следующий шаг от currentStep
                        // Но для ADD_KEYWORDS следующий шаг всегда CHOOSE_COLLECTIONS (независимо от пути)
                        if (
                            currentStep === BEGIN_WORK_STEPS_IDS.ADD_KEYWORDS ||
                            currentStepStr === BEGIN_WORK_STEPS_IDS.ADD_KEYWORDS.trim().toLowerCase()
                        ) {
                            stepToUpdate = BEGIN_WORK_STEPS_IDS.CHOOSE_COLLECTIONS as StepsType;
                            console.log(
                                `🔄 [UpdateBeginWorkStepsProvider] Переход от ADD_KEYWORDS к CHOOSE_COLLECTIONS`
                            );
                        } else {
                            stepToUpdate = getBeginWorkNextStepId(currentStep);
                            console.log(
                                `🔄 [UpdateBeginWorkStepsProvider] Обычный переход: от ${currentStep} к ${stepToUpdate}`
                            );
                        }
                    }
                } else {
                    // Если не указан currentStep, это ошибка для явных переходов
                    // Не должны вызывать updateBeginWorkStep без currentStep
                    console.error(
                        `❌ [UpdateBeginWorkStepsProvider] currentStep не указан! Это ошибка для явных переходов.`
                    );
                    console.error(`❌ [UpdateBeginWorkStepsProvider] onboardingStep=${onboardingStep}`);
                    stepToUpdate = null;
                }

                console.log(`🔍 [UpdateBeginWorkStepsProvider] stepToUpdate определен: ${stepToUpdate}`);
                if (stepToUpdate) {
                    // Защита от отправки 'completed' - это должно происходить только после оплаты
                    if (stepToUpdate === BEGIN_WORK_STEPS_IDS.COMPLETED) {
                        console.error(
                            `❌ [UpdateBeginWorkStepsProvider] Попытка установить 'completed' вручную запрещена!`
                        );
                        console.error(
                            `❌ [UpdateBeginWorkStepsProvider] currentStep=${currentStep}, onboardingStep=${onboardingStep}, stepToUpdate=${stepToUpdate}`
                        );
                        return; // Не отправляем запрос
                    }

                    try {
                        // Получаем текущий шаг пользователя для логирования
                        const currentUserStep = useUserStore.getState().onboardingStepName;
                        console.log(
                            `🔄 [UpdateBeginWorkStepsProvider] Обновляем шаг: ${currentUserStep} → ${stepToUpdate}`
                        );
                        console.log(
                            `📤 [UpdateBeginWorkStepsProvider] Отправляем запрос на /api/accounts/update-onboarding-step/ с step=${stepToUpdate}`
                        );
                        console.log(
                            `📤 [UpdateBeginWorkStepsProvider] Параметры: currentStep=${currentStep}, onboardingStep=${onboardingStep}, stepToUpdate=${stepToUpdate}`
                        );
                        console.log(
                            `📤 [UpdateBeginWorkStepsProvider] Тип stepToUpdate: ${typeof stepToUpdate}, значение: ${JSON.stringify(
                                stepToUpdate
                            )}`
                        );

                        // Дополнительная проверка перед отправкой (проверка на completed уже есть выше)
                        if (!stepToUpdate) {
                            console.error(
                                `❌ [UpdateBeginWorkStepsProvider] CRITICAL ERROR: stepToUpdate is null/undefined, not sending request!`
                            );
                            return;
                        }

                        console.log(
                            `🔄 [UpdateBeginWorkStepsProvider] ВЫЗЫВАЕМ updateStep с stepToUpdate=${stepToUpdate}`
                        );
                        await updateStep(stepToUpdate);
                        console.log(`✅ [UpdateBeginWorkStepsProvider] Шаг успешно обновлен на ${stepToUpdate}`);
                        console.log(`🔄 [UpdateBeginWorkStepsProvider] ВЫЗЫВАЕМ onSuccess`);
                        await onSuccess?.();
                        console.log(`✅ [UpdateBeginWorkStepsProvider] onSuccess выполнен`);
                    } catch (error: any) {
                        console.error(`❌ [UpdateBeginWorkStepsProvider] Ошибка обновления шага:`, error);
                        console.error(`❌ [UpdateBeginWorkStepsProvider] Детали ошибки:`, {
                            status: error?.response?.status,
                            statusText: error?.response?.statusText,
                            data: error?.response?.data,
                            message: error?.message,
                        });

                        // Проверяем, не является ли ошибка следствием того, что шаг уже обновлен
                        const errorMessage =
                            error?.response?.data?.error || error?.response?.data?.message || error?.message || '';
                        const errorData = error?.response?.data || {};

                        console.log(`🔍 [UpdateBeginWorkStepsProvider] Анализ ошибки:`, {
                            errorMessage,
                            errorData,
                            status: error?.response?.status,
                        });

                        const isAlreadyUpdated =
                            errorMessage.includes('Cannot skip steps') ||
                            errorMessage.includes('already') ||
                            errorMessage.includes('From format step') ||
                            errorMessage.includes('can only go to') ||
                            (error?.response?.status === 400 && errorMessage.includes('can only go to'));

                        // Если ошибка связана с тем, что шаг уже обновлен или это валидационная ошибка развилки - не показываем ошибку
                        if (!isAlreadyUpdated) {
                            console.error(
                                `❌ [UpdateBeginWorkStepsProvider] Показываем ошибку пользователю: ${errorMessage}`
                            );
                            enqueueSnackbar(`Ошибка перехода: ${errorMessage || 'Неизвестная ошибка'}`, {
                                variant: 'error',
                            });
                        } else {
                            console.log(
                                `ℹ️ [UpdateBeginWorkStepsProvider] Ошибка проигнорирована (шаг уже обновлен или валидация): ${errorMessage}`
                            );
                            // Если шаг уже обновлен, все равно вызываем onSuccess для перехода
                            await onSuccess?.();
                        }
                    }
                } else {
                    console.warn(`⚠️ [UpdateBeginWorkStepsProvider] Не удалось определить следующий шаг`);
                    console.warn(
                        `⚠️ [UpdateBeginWorkStepsProvider] currentStep=${currentStep}, realCurrentStep=${realCurrentStep}, stepToUpdate=${stepToUpdate}`
                    );
                    // НЕ вызываем onSuccess если stepToUpdate не определен - это ошибка
                    // Запрос должен отправляться всегда, если currentStep передан правильно
                    console.error(
                        `❌ [UpdateBeginWorkStepsProvider] CRITICAL: stepToUpdate не определен, но currentStep=${currentStep}. Это ошибка в логике!`
                    );
                }
            }
        );

        return () => {
            if (subscriber && subscriber.unsubscribe) {
                subscriber.unsubscribe();
            }
        };
    }, [isOnboardingCompleted, isAdminOverride, updateStep, enqueueSnackbar]);

    useLayoutEffect(() => {
        const currentPath = window.location.pathname;

        // Исключаем страницы, которые не должны перенаправляться во время онбординга
        const excludedPaths = ['/leadstream', '/payment', '/tariff', '/profile', '/projects'];
        const isExcludedPath = excludedPaths.some((path) => currentPath.startsWith(path));

        // Если онбординг завершен - перенаправляем на Feed
        if (isOnboardingCompleted) {
            console.log('🚀 [UpdateBeginWorkStepsProvider] Онбординг завершен, текущий путь:', currentPath);
            if (currentPath.startsWith('/begin_work')) {
                console.log('🚀 [UpdateBeginWorkStepsProvider] Перенаправляем с /begin_work на /feed');
                // Используем window.location для более надежного редиректа
                window.location.href = '/feed';
                return;
            }
            return;
        }

        if (!hasSessionSnapshot) {
            console.log('⏳ [UpdateBeginWorkStepsProvider] Ждем данные пользователя перед переключением шага');
            return;
        }

        // Не перенаправляем, если идет обновление шага (isLoading) или шаг только что обновлен
        // Это предотвращает конфликт с явными переходами в onSuccess
        if (isLoading || justUpdatedStepRef.current) {
            console.log(
                '⏳ [UpdateBeginWorkStepsProvider] Идет обновление шага или шаг только что обновлен, пропускаем автоматический редирект'
            );
            return;
        }

        // Не перенаправляем, если пользователь на исключенной странице
        if (isExcludedPath) {
            console.log(
                `⏭️ [UpdateBeginWorkStepsProvider] Страница ${currentPath} исключена из автоматического редиректа`
            );
            return;
        }

        const stepTypeByOrder = getBeginWorkStepByOrder(onboardingStep);

        // Автоматически переходим на страницу шага, если есть ссылка
        // НО только если мы не на правильной странице
        if (stepTypeByOrder?.link) {
            const targetPath = stepTypeByOrder.link;

            // Пропускаем автоматический редирект, если мы уже на правильной странице
            if (currentPath === targetPath) {
                return;
            }

            // Разрешаем находиться на страницах после create_project (keywords, collections, chats)
            // даже если onboardingStep указывает на create_project
            const allowedPathsAfterCreateProject = [
                '/begin_work/keywords',
                '/begin_work/collections',
                '/begin_work/chats',
                '/begin_work/chats_and_channels',
                ROUTES_URL.PAYMENT,
            ];
            const isOnAllowedPathAfterCreateProject = allowedPathsAfterCreateProject.some((path) =>
                currentPath.startsWith(path)
            );

            // Если пользователь на странице после create_project и текущий шаг create_project,
            // не перенаправляем обратно на create_project
            if (isOnAllowedPathAfterCreateProject && stepTypeByOrder?.id === BEGIN_WORK_STEPS_IDS.CREATE_PROJECT) {
                console.log(
                    `⏭️ [UpdateBeginWorkStepsProvider] Пользователь на странице после create_project (${currentPath}), не перенаправляем обратно`
                );
                return;
            }

            // Перенаправляем только если мы явно не на нужной странице
            console.log(`🔄 [UpdateBeginWorkStepsProvider] Автоматический редирект: ${currentPath} → ${targetPath}`);
            goTo(targetPath);
        }
    }, [hasSessionSnapshot, isOnboardingCompleted, onboardingStep, goTo, isLoading]);

    return <LoadingWrapper isLoading={isLoading}>{children}</LoadingWrapper>;
}
